import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCertificationApproval } from '../../hooks/useCertificationApproval';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { isDemoMode, disableDemoMode } from '../../services/mockData';
import { UserRole } from '../../types';
import {
  canViewTeamExpenses,
  canViewAllExpenses,
  canManageUsers,
  canAccessShiftRoster,
  canViewShiftRoster,
  canManageShiftMaster,
  canAccessShiftReports,
  canApproveFinance,
  getRoleDisplayName
} from '../../utils/rolePermissions';
import {
  getNominationMenuItems
} from '../../utils/nominationPermissions';
import { useNominationRole } from '../../hooks/useNominationRole';
import { isLeaveApprover as isLeaveApproverRole } from '../../services/leaveVisibility';
import {
  mapUserRoleToPMSRole,
  getPMSMenuItems,
  hasModuleAccess
} from '../../utils/pmsPermissions';
import SidebarFlyout from './SidebarFlyout';
import { MOODMATE_ENABLED } from '../../config/features';
import './Sidebar.css';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const { user, signOut, isDemoMode: isDemo } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [emsExpanded, setEmsExpanded] = useState(true);
  const [nominationsExpanded, setNominationsExpanded] = useState(false);
  const [certificationsExpanded, setCertificationsExpanded] = useState(false);
  const [performanceExpanded, setPerformanceExpanded] = useState(false);
  const [timesheetExpanded, setTimesheetExpanded] = useState(false);
  const [leaveExpanded, setLeaveExpanded] = useState(false);
  const [shiftRosterExpanded, setShiftRosterExpanded] = useState(false);
  const [attendanceExpanded, setAttendanceExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const isCompactSidebar = isCollapsed && !isMobileOpen;
  const showSidebarLabels = !isCollapsed || isMobileOpen;

  // Flyout state for collapsed sidebar
  const [activeFlyout, setActiveFlyout] = useState<string | null>(null);
  const emsTriggerRef = useRef<HTMLButtonElement>(null);
  const timesheetTriggerRef = useRef<HTMLButtonElement>(null);
  const nominationsTriggerRef = useRef<HTMLButtonElement>(null);
  const certificationsTriggerRef = useRef<HTMLButtonElement>(null);
  const performanceTriggerRef = useRef<HTMLButtonElement>(null);
  const shiftRosterTriggerRef = useRef<HTMLButtonElement>(null);
  const attendanceTriggerRef = useRef<HTMLButtonElement>(null);
  const leaveTriggerRef = useRef<HTMLButtonElement>(null);

  // Close flyout when sidebar expands or route changes
  const closeFlyout = useCallback(() => setActiveFlyout(null), []);

  useEffect(() => { closeFlyout(); }, [isCollapsed, closeFlyout]);
  useEffect(() => { closeFlyout(); }, [location.pathname, closeFlyout]);

  useEffect(() => { setIsMobileOpen(false); }, [location.pathname]);
  const { nominationRole } = useNominationRole(user);

  useEffect(() => {
    const emsRoutes = ['/dashboard', '/expenses', '/manager', '/admin', '/super-admin'];
    if (emsRoutes.some(route => location.pathname.startsWith(route))) setEmsExpanded(true);
    if (location.pathname.startsWith('/nominations')) setNominationsExpanded(true);
    if (location.pathname.startsWith('/certifications')) setCertificationsExpanded(true);
    if (location.pathname.startsWith('/performance')) setPerformanceExpanded(true);
    if (location.pathname.startsWith('/timesheet')) setTimesheetExpanded(true);
    if (location.pathname.startsWith('/shift-roster')) setShiftRosterExpanded(true);
    if (location.pathname.startsWith('/attendance')) setAttendanceExpanded(true);
    if (location.pathname.startsWith('/leave')) setLeaveExpanded(true);
  }, [location.pathname]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSignOut = async () => {
    try {
      setUserMenuOpen(false);
      await signOut();
      if (isDemoMode()) disableDemoMode();
      navigate('/login');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPER_ADMIN: return 'role-badge super-admin';
      case UserRole.FINANCE_ADMIN: return 'role-badge admin';
      case UserRole.MANAGER: return 'role-badge manager';
      default: return 'role-badge employee';
    }
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  /*
   * Who sees which Attendance entries.
   *
   * Two different questions, and conflating them is what hid the team screens from managers: every
   * entry used to sit behind canManageUsers, which is Super_Admin/HR_Admin only.
   *
   *   attendanceAdmin   may OPERATE the system — Raw Punches, Device Sync, everybody's rows
   *   attendanceTeam    may see OTHER PEOPLE, scoped by AdminAttendance to their own reports
   *
   * A manager is the second and not the first. The labels differ too: "Admin Attendance" is the
   * wrong name for a page showing somebody their own team, so the entry renames itself — matching
   * the managerTitle the route already switches to in AttendancePage.
   */
  const attendanceAdmin = Boolean(user?.role && canManageUsers(user.role));
  const attendanceManager = !attendanceAdmin && Boolean(user?.role && canViewTeamExpenses(user.role));
  const attendanceTeam = attendanceAdmin || attendanceManager;
  const teamAttendanceLabel = attendanceManager ? 'Team Attendance' : 'Admin Attendance';
  /* Decides whether the approvals child appears — see certificationMenuItems. */
  const certificationApproval = useCertificationApproval();
  const isNominationsActive = () => location.pathname.startsWith('/nominations');
  const isCertificationsActive = () => location.pathname.startsWith('/certifications');
  const isPerformanceActive = () => location.pathname.startsWith('/performance');
  const isTimesheetActive = () => location.pathname.startsWith('/timesheet');
  const isLeaveActive = () => location.pathname.startsWith('/leave');

  // Icons
  const HomeIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>);
  const EMSIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>);
  const MyExpensesIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>);
  const CheckSquareIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>);
  const BriefcaseIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>);
  const ChevronDownIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>);
  const TrophyIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>);
  const TargetIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>);
  const ClockIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>);
  const CalendarIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>);

  const emsSubItems = [
    { path: '/expenses/my', icon: <MyExpensesIcon />, label: 'My Expenses', show: true },
    { path: '/manager', icon: <CheckSquareIcon />, label: 'Team Approvals', show: user?.role && canViewTeamExpenses(user.role) },
    { path: '/admin', icon: <BriefcaseIcon />, label: 'Finance', show: user?.role && canViewAllExpenses(user.role) },
  ];

  const isLeaveManager = user?.role === UserRole.MANAGER || user?.role === UserRole.HR_ADMIN || user?.role === UserRole.SUPER_ADMIN;
  const isLeaveHR = user?.role === UserRole.HR_ADMIN || user?.role === UserRole.SUPER_ADMIN;
  // Finance Admin approves too, so this is a wider set than isLeaveManager. Read from
  // leaveVisibility rather than re-listed here, so the menu can never offer the approvals
  // queue to a role the queue itself then treats as having nothing to act on.
  const isLeaveApprover = isLeaveApproverRole({ email: user?.email, role: user?.role });
  const smallIcon = (children: React.ReactNode) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>);
  const leaveSubItems = [
    { path: '/leave/dashboard', icon: smallIcon(<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>), label: 'Leave Dashboard', show: true },
    { path: '/leave/apply', icon: smallIcon(<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" /></>), label: 'Apply Leave', show: true },
    { path: '/leave/approvals', icon: smallIcon(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>), label: 'Pending Approvals', show: isLeaveApprover },
    // Visible to everyone: an employee reads their own Comp-Off ledger and raises their
    // own cancellations here; the pages decide what is editable per role.
    { path: '/leave/comp-off', icon: smallIcon(<><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></>), label: 'Apply Comp-Off', show: true },
    { path: '/leave/cancellation', icon: smallIcon(<><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>), label: 'Leave Cancellation', show: true },
    // Managers only — narrower than isLeaveManager, which also covers HR and Super Admin.
    // Those two grant org-wide from Apply Comp-Off's HR tab instead; see the route comment.
    // Grant Comp-Off is gone and gets no replacement entry: its successor, Comp-Off History, is a
    // tab on Apply Comp-Off (visible to managers) rather than a page of its own.
    { path: '/leave/team-matrix', icon: smallIcon(<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>), label: 'Team Leave Matrix', show: isLeaveManager },
    { path: '/leave/policies', icon: smallIcon(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>), label: 'Leave Policies', show: isLeaveHR },
    { path: '/leave/hr-operations', icon: smallIcon(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>), label: 'HR Operations', show: isLeaveHR },
    { path: '/leave/reports', icon: smallIcon(<><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>), label: 'Reports', show: isLeaveHR },
  ];

  // getNominationMenuItems returns exactly the role-gated Award Nomination
  // sub-menu items (see Property 15: Sidebar menu filtering by role). Voting
  // Polls is a separate, always-visible feature that is appended here in the
  // Sidebar rather than inside getNominationMenuItems so that utility stays
  // an exact match for the role-permission spec it is tested against.
  const nominationMenuItems = [
    ...getNominationMenuItems(nominationRole),
    { path: '/voting', label: 'Voting Polls' }
  ];

  /*
   * The two Certifications pages, plus the approval queue for whoever holds it.
   *
   * Two pages rather than one with tabs: applying for a reimbursement and maintaining a
   * qualification history are different tasks, visited for different reasons, and the module is
   * listed the way Award Nominations and Performance are — a parent with clickable children.
   *
   * The approvals entry is added only for somebody who can actually decide. An existing admin
   * reaches certifications through the admin area they already use, so it is there for the
   * ADDITIONAL approver configured in Settings, who has no other queue to reach it from.
   */
  const certificationMenuItems = [
    { path: '/certifications/reimbursement', label: 'Apply for Reimbursement' },
    { path: '/certifications/mine', label: 'My Certifications' },
    { path: '/certifications/all', label: 'All Certifications' },
    /*
     * One entry per stage, each gated independently and shown to whoever can act on it.
     *
     * Additive by construction: a manager who is also the configured certification admin gets
     * BOTH entries, because these are three separate conditions rather than branches of one
     * decision. Nothing here names a person — the gates read the session's role and the
     * configured admin from Settings, so access follows a change in either automatically.
     */
    ...(certificationApproval.canViewManagerQueue
      ? [{ path: '/certifications/approvals', label: 'Certification Approvals' }]
      : []),
    ...(certificationApproval.canViewAdminQueue
      ? [{ path: '/certifications/admin-approvals', label: 'Certificate Admin Approval' }]
      : []),
    /* Certification claims only. Deliberately NOT beside the EMS Finance expense queue: the two
       flows share no table, no status model and no resolver, and mixing them on one screen is
       what the separation exists to prevent. */
    ...(certificationApproval.canViewFinanceQueue
      ? [{ path: '/certifications/finance', label: 'Finance Reimbursements' }]
      : []),
  ];

  const pmsRole = user?.role ? mapUserRoleToPMSRole(user.role) : null;
  const pmsMenuItems = pmsRole ? getPMSMenuItems(pmsRole) : [];

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  };


  return (
    <>
      {/* Mobile Menu Button */}
      <button className="mobile-menu-button" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label="Toggle menu">
        <span className={`hamburger ${isMobileOpen ? 'open' : ''}`}><span></span><span></span><span></span></span>
      </button>
      {isMobileOpen && <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)} />}

      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-header">
          <div className="sidebar-brand" onClick={() => navigate('/home')}>
            <img src="/ci-logo.png" alt="Company Logo" className="sidebar-logo" />
            {showSidebarLabels && <span className="sidebar-title">Creyente One</span>}
          </div>
        </div>

        {/* Edge collapse/expand toggle */}
        <button
          className={`sidebar-edge-toggle ${isCollapsed ? 'expand' : ''}`}
          onClick={onToggle}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={isCollapsed ? '9 18 15 12 9 6' : '15 18 9 12 15 6'} />
          </svg>
        </button>

        {(isDemo || isDemoMode()) && showSidebarLabels && (
          <div className="demo-badge"><span className="demo-icon">🎮</span> Demo Mode</div>
        )}

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-section">
            {showSidebarLabels && <span className="nav-section-title">Navigation</span>}

            {/* Home */}
            <button onClick={() => navigate('/home')} className={`sidebar-nav-item ${isActive('/home') ? 'active home-active' : ''}`} aria-label="Home">
              <span className="nav-item-icon"><HomeIcon /></span>
              {showSidebarLabels && <span className="nav-item-label">Home</span>}
            </button>


            {/* EMS Dropdown */}
            {(!user?.role || hasModuleAccess(user.role, 'EMS')) && (
            <div className="nav-dropdown">
              <button
                ref={emsTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'ems' ? null : 'ems');
                  } else {
                    setEmsExpanded(!emsExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${emsExpanded && showSidebarLabels ? 'section-open' : ''}`}
              >
                <span className="nav-item-icon"><EMSIcon /></span>
                {showSidebarLabels && (<><span className="nav-item-label">EMS</span><span className={`dropdown-arrow ${emsExpanded ? 'expanded' : ''}`}><ChevronDownIcon /></span></>)}
              </button>
              {showSidebarLabels && emsExpanded && (
                <div className="nav-submenu">
                  {emsSubItems.filter(item => item.show).map((item) => (
                    <button key={item.path} onClick={() => navigate(item.path)} className={`sidebar-nav-item submenu-item ${isActive(item.path) ? 'active' : ''}`}>
                      <span className="nav-item-icon">{item.icon}</span>
                      <span className="nav-item-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Timesheets Dropdown */}
            <div className="nav-dropdown">
              <button
                ref={timesheetTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'timesheet' ? null : 'timesheet');
                  } else {
                    setTimesheetExpanded(!timesheetExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isTimesheetActive() ? 'active' : ''} ${timesheetExpanded && showSidebarLabels ? 'section-open' : ''}`}
              >
                <span className="nav-item-icon"><ClockIcon /></span>
                {showSidebarLabels && (<><span className="nav-item-label">Timesheets</span><span className={`dropdown-arrow ${timesheetExpanded ? 'expanded' : ''}`}><ChevronDownIcon /></span></>)}
              </button>
              {showSidebarLabels && timesheetExpanded && (
                <div className="nav-submenu">
                  <button onClick={() => navigate('/timesheet')} className={`sidebar-nav-item submenu-item ${isActive('/timesheet') && !isActive('/timesheet/team') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
                    <span className="nav-item-label">My Timesheet</span>
                  </button>
                  {user?.role && canViewTeamExpenses(user.role) && (
                    <button onClick={() => navigate('/timesheet/team')} className={`sidebar-nav-item submenu-item ${isActive('/timesheet/team') ? 'active' : ''}`}>
                      <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></span>
                      <span className="nav-item-label">Team Timesheets</span>
                    </button>
                  )}
                  {user?.role && (user.role === UserRole.SUPER_ADMIN || user.role === UserRole.HR_ADMIN) && (
                    <button onClick={() => navigate('/timesheet/projects')} className={`sidebar-nav-item submenu-item ${isActive('/timesheet/projects') ? 'active' : ''}`}>
                      <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg></span>
                      <span className="nav-item-label">Projects & Tasks</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Leave Management Dropdown */}
            <div className="nav-dropdown">
              <button
                ref={leaveTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'leave' ? null : 'leave');
                  } else {
                    setLeaveExpanded(!leaveExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isLeaveActive() ? 'active' : ''} ${leaveExpanded && showSidebarLabels ? 'section-open' : ''}`}
                aria-label="Leave Roster"
              >
                <span className="nav-item-icon"><CalendarIcon /></span>
                {showSidebarLabels && (<><span className="nav-item-label">Leave Roster</span><span className={`dropdown-arrow ${leaveExpanded ? 'expanded' : ''}`}><ChevronDownIcon /></span></>)}
              </button>
              {showSidebarLabels && leaveExpanded && (
                <div className="nav-submenu">
                  {leaveSubItems.filter(item => item.show).map((item) => (
                    <button key={item.path} onClick={() => navigate(item.path)} className={`sidebar-nav-item submenu-item ${isActive(item.path) ? 'active' : ''}`}>
                      <span className="nav-item-icon">{item.icon}</span>
                      <span className="nav-item-label">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Shift Roster Dropdown — visible to everyone, since Shift Details is now a
                read-only view for Employees. The write-only entries inside are still gated. */}
            {user?.role && canViewShiftRoster(user.role) && (
            <div className="nav-dropdown">
              <button
                ref={shiftRosterTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'shiftRoster' ? null : 'shiftRoster');
                  } else {
                    setShiftRosterExpanded(!shiftRosterExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isActive('/shift-roster') ? 'active' : ''} ${shiftRosterExpanded && showSidebarLabels ? 'section-open' : ''}`}
                aria-label="Shift Roster"
              >
                <span className="nav-item-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                    <path d="M8 14h.01"/>
                    <path d="M12 14h.01"/>
                    <path d="M16 14h.01"/>
                  </svg>
                </span>
                {showSidebarLabels && (
                  <>
                    <span className="nav-item-label">Shift Roster</span>
                    <span className={`dropdown-arrow ${shiftRosterExpanded ? 'expanded' : ''}`}>
                      <ChevronDownIcon />
                    </span>
                  </>
                )}
              </button>
              {showSidebarLabels && shiftRosterExpanded && (
                <div className="nav-submenu">
                  <button onClick={() => navigate('/shift-roster/shift-details')} className={`sidebar-nav-item submenu-item ${isActive('/shift-roster/shift-details') ? 'active' : ''}`}>
                    <span className="nav-item-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></svg>
                    </span>
                    <span className="nav-item-label">Shift Details</span>
                  </button>
                  {/* Shift Configuration creates and edits the shift definitions themselves,
                      which is canManageShiftMaster — HR and Super Admin only. A Manager could
                      previously click through to it and land on an empty page, because the tab
                      body is gated but this link was not. */}
                  {user?.role && canManageShiftMaster(user.role) && (
                  <button onClick={() => navigate('/shift-roster/shift-configuration')} className={`sidebar-nav-item submenu-item ${isActive('/shift-roster/shift-configuration') ? 'active' : ''}`}>
                    <span className="nav-item-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                    </span>
                    <span className="nav-item-label">Shift Configuration</span>
                  </button>
                  )}
                  {/* Bulk Override exists only to write, so it follows the edit permission
                      rather than the view one. */}
                  {user?.role && canAccessShiftRoster(user.role) && (
                  <button onClick={() => navigate('/shift-roster/bulk-override')} className={`sidebar-nav-item submenu-item ${isActive('/shift-roster/bulk-override') ? 'active' : ''}`}>
                    <span className="nav-item-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                    </span>
                    <span className="nav-item-label">Bulk Override</span>
                  </button>
                  )}
                  {/* Reports exports the whole directory, so it is HR/Super only — the same gate
                      its route carries. */}
                  {user?.role && canAccessShiftReports(user.role) && (
                  <button onClick={() => navigate('/shift-roster/reports')} className={`sidebar-nav-item submenu-item ${isActive('/shift-roster/reports') ? 'active' : ''}`}>
                    <span className="nav-item-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></svg>
                    </span>
                    <span className="nav-item-label">Reports</span>
                  </button>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Attendance Dropdown — visible to everyone, because everyone has attendance.
                My Attendance is the only entry an Employee sees; the admin entries below are
                gated on canManageUsers, the same permission the panes themselves check. */}
            <div className="nav-dropdown">
              <button
                ref={attendanceTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'attendance' ? null : 'attendance');
                  } else {
                    setAttendanceExpanded(!attendanceExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isActive('/attendance') ? 'active' : ''} ${attendanceExpanded && showSidebarLabels ? 'section-open' : ''}`}
                aria-label="Attendance"
              >
                <span className="nav-item-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>
                </span>
                {showSidebarLabels && (
                  <>
                    <span className="nav-item-label">Attendance</span>
                    <span className={`dropdown-arrow ${attendanceExpanded ? 'expanded' : ''}`}>
                      <ChevronDownIcon />
                    </span>
                  </>
                )}
              </button>
              {showSidebarLabels && attendanceExpanded && (
                <div className="nav-submenu">
                  <button onClick={() => navigate('/attendance/my-attendance')} className={`sidebar-nav-item submenu-item ${isActive('/attendance/my-attendance') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg></span>
                    <span className="nav-item-label">My Attendance</span>
                  </button>
                  {/* Everybody can apply; the page shows an Approvals tab to those who have one. */}
                  <button onClick={() => navigate('/attendance/regularization')} className={`sidebar-nav-item submenu-item ${isActive('/attendance/regularization') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg></span>
                    <span className="nav-item-label">Regularization</span>
                  </button>
                  {/* Other people's attendance: admins see everyone, a manager sees their reports. */}
                  {attendanceTeam && (
                  <button onClick={() => navigate('/attendance/admin')} className={`sidebar-nav-item submenu-item ${isActive('/attendance/admin') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></span>
                    <span className="nav-item-label">{teamAttendanceLabel}</span>
                  </button>
                  )}
                  {/* Operating the device itself stays with HR and Super Admin. */}
                  {attendanceAdmin && (
                    <>
                  <button onClick={() => navigate('/attendance/raw-punches')} className={`sidebar-nav-item submenu-item ${isActive('/attendance/raw-punches') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg></span>
                    <span className="nav-item-label">Raw Punches</span>
                  </button>
                  <button onClick={() => navigate('/attendance/device-sync')} className={`sidebar-nav-item submenu-item ${isActive('/attendance/device-sync') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg></span>
                    <span className="nav-item-label">Device Sync</span>
                  </button>
                  <button onClick={() => navigate('/attendance/reports')} className={`sidebar-nav-item submenu-item ${isActive('/attendance/reports') ? 'active' : ''}`}>
                    <span className="nav-item-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></svg></span>
                    <span className="nav-item-label">Reports</span>
                  </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Award Nominations Dropdown */}
            {(!user?.role || hasModuleAccess(user.role, 'AWARD_NOMINATION')) && (
            <div className="nav-dropdown">
              <button
                ref={nominationsTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'nominations' ? null : 'nominations');
                  } else {
                    setNominationsExpanded(!nominationsExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isNominationsActive() ? 'active' : ''} ${nominationsExpanded && showSidebarLabels ? 'section-open' : ''}`}
              >
                <span className="nav-item-icon"><TrophyIcon /></span>
                {showSidebarLabels && (<><span className="nav-item-label">Award Nominations</span><span className={`dropdown-arrow ${nominationsExpanded ? 'expanded' : ''}`}><ChevronDownIcon /></span></>)}
              </button>
              {showSidebarLabels && nominationsExpanded && (
                <div className="nav-submenu">
                  {nominationMenuItems.map((item) => {
                    const iconMap: Record<string, React.ReactElement> = {
                      '/nominations/feed': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2"/></svg>,
                      '/nominations/my': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
                      '/nominations/new': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
                      '/voting': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
                      '/nominations/review': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
                      '/nominations/admin': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
                    };
                    return (
                      <button key={item.path} onClick={() => navigate(item.path)} className={`sidebar-nav-item submenu-item ${isActive(item.path) ? 'active' : ''}`}>
                        <span className="nav-item-icon">{iconMap[item.path] || <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}</span>
                        <span className="nav-item-label">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Performance Dropdown */}
            <div className="nav-dropdown">
              <button
                ref={performanceTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'performance' ? null : 'performance');
                  } else {
                    setPerformanceExpanded(!performanceExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isPerformanceActive() ? 'active' : ''} ${performanceExpanded && showSidebarLabels ? 'section-open' : ''}`}
              >
                <span className="nav-item-icon"><TargetIcon /></span>
                {showSidebarLabels && (<><span className="nav-item-label">Performance</span><span className={`dropdown-arrow ${performanceExpanded ? 'expanded' : ''}`}><ChevronDownIcon /></span></>)}
              </button>
              {showSidebarLabels && performanceExpanded && (
                <div className="nav-submenu">
                  {pmsMenuItems.map((item) => {
                    const iconMap: Record<string, React.ReactElement> = {
                      '/performance/objectives': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
                      '/performance/reviews': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
                      '/performance/team': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
                      '/performance/approvals': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
                      '/performance/team-reviews': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
                      '/performance/annual-cycle': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
                      '/performance/analytics': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
                      '/performance/review-cycles': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
                      '/performance/reports': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
                      '/performance/lock-management': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
                    };
                    return (
                      <button key={item.path} onClick={() => navigate(item.path)} className={`sidebar-nav-item submenu-item ${isActive(item.path) ? 'active' : ''}`}>
                        <span className="nav-item-icon">{iconMap[item.path] || <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}</span>
                        <span className="nav-item-label">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/*
              * Certifications — a parent with two clickable children.
              *
              * Both pages are separate destinations with their own routes. Neither is a tab of
              * the other: Apply for Reimbursement is a claim and its progress, All
              * Certifications is the repository of what somebody holds.
              *
              * No role gate on the parent. Every employee has certifications to record and can
              * claim one; each page decides what its viewer may see.
              */}
            <div className="nav-dropdown">
              <button
                ref={certificationsTriggerRef}
                onClick={() => {
                  if (isCompactSidebar) {
                    setActiveFlyout(activeFlyout === 'certifications' ? null : 'certifications');
                  } else {
                    setCertificationsExpanded(!certificationsExpanded);
                  }
                }}
                className={`sidebar-nav-item dropdown-toggle ${isCertificationsActive() ? 'active' : ''} ${certificationsExpanded && showSidebarLabels ? 'section-open' : ''}`}
              >
                <span className="nav-item-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" /></svg></span>
                {showSidebarLabels && (<><span className="nav-item-label">Certifications</span><span className={`dropdown-arrow ${certificationsExpanded ? 'expanded' : ''}`}><ChevronDownIcon /></span></>)}
              </button>
              {showSidebarLabels && certificationsExpanded && (
                <div className="nav-submenu">
                  {certificationMenuItems.map((item) => {
                    const iconMap: Record<string, React.ReactElement> = {
                      '/certifications/reimbursement': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
                      '/certifications/mine': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M6 21v-1a6 6 0 0 1 12 0v1"/></svg>,
                      '/certifications/all': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
                      '/certifications/approvals': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
                    };
                    return (
                      <button key={item.path} onClick={() => navigate(item.path)} className={`sidebar-nav-item submenu-item ${isActive(item.path) ? 'active' : ''}`}>
                        <span className="nav-item-icon">{iconMap[item.path] || <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>}</span>
                        <span className="nav-item-label">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Ask Creyon */}
            <button onClick={() => navigate('/chat')} className={`sidebar-nav-item ${isActive('/chat') ? 'active' : ''}`} aria-label="Ask Creyon">
              <span className="nav-item-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></span>
              {showSidebarLabels && <span className="nav-item-label">Ask Creyon</span>}
            </button>

          </div>
        </nav>

        {/* Compact User Footer with Popup */}
        <div className="sidebar-footer" ref={userMenuRef}>
          {user && (
            <div className={`sidebar-user-compact ${userMenuOpen ? 'active' : ''}`} onClick={() => setUserMenuOpen(!userMenuOpen)}>
              <div className="sidebar-user-avatar">{getInitials(user.name)}</div>
              {showSidebarLabels && (
                <div className="sidebar-user-info">
                  <span className="sidebar-user-name">{user.name}</span>
                  <span className={getRoleBadgeClass(user.role)}>{getRoleDisplayName(user.role)}</span>
                </div>
              )}
              {showSidebarLabels && (
                <svg className="sidebar-user-dots" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                </svg>
              )}
            </div>
          )}

          {/* User Popup Menu */}
          {userMenuOpen && (
            <div className="sidebar-user-popup">
              <div className="sidebar-popup-header">
                <div className="sidebar-popup-avatar">{user ? getInitials(user.name) : '?'}</div>
                <div className="sidebar-popup-info">
                  <span className="sidebar-popup-name">{user?.name}</span>
                  <span className="sidebar-popup-email">{user?.email}</span>
                </div>
              </div>
              <div className="sidebar-popup-divider" />
              <button className="sidebar-popup-item dark-mode-popup-toggle" onClick={toggleDarkMode}>
                <span>{isDarkMode ? '☀️' : '🌙'}</span>
                <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                <span className={`toggle-switch-mini ${isDarkMode ? 'active' : ''}`} />
              </button>
              <div className="sidebar-popup-divider" />
              {/*
                * Everyone's own record, so no role test.
                *
                * The onboarding form is reached through an emailed link that is burned on
                * submit, which left people unable to see what they had declared about
                * themselves the moment they finished declaring it. The SERVER decides whose
                * record this opens — see getEmployeeSelfServiceProfile.
                */}
              <button
                className="sidebar-popup-item"
                onClick={() => { navigate('/my-information'); setUserMenuOpen(false); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                </svg>
                My Information
              </button>
              {/* The personal Mood Journal is a section of My Information — same self-only scope.
                  Hidden where MoodMate is disabled for the environment (e.g. prod). */}
              {MOODMATE_ENABLED && (
                <button
                  className="sidebar-popup-item"
                  onClick={() => { navigate('/my-information/mood-journal'); setUserMenuOpen(false); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                  Mood Journal
                </button>
              )}
              <div className="sidebar-popup-divider" />
              {user?.role && canManageUsers(user.role) && (
                <>
                  <button className="sidebar-popup-item" onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    Settings
                  </button>
                  <div className="sidebar-popup-divider" />
                </>
              )}
              <button className="sidebar-popup-item" onClick={handleSignOut}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign Out
              </button>
              <div className="sidebar-popup-copyright">© 2026 Creyente InfoTech</div>
            </div>
          )}
        </div>
      </aside>

      {/* Portal-based flyout menus for collapsed sidebar */}
      {isCompactSidebar && (
        <>
          <SidebarFlyout
            items={emsSubItems.filter(item => item.show).map(item => ({ path: item.path, icon: item.icon, label: item.label }))}
            triggerRef={emsTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'ems'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          <SidebarFlyout
            items={leaveSubItems.filter(item => item.show).map(item => ({ path: item.path, icon: item.icon, label: item.label }))}
            triggerRef={leaveTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'leave'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          <SidebarFlyout
            items={[
              { path: '/timesheet', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, label: 'My Timesheet' },
              ...(user?.role && canViewTeamExpenses(user.role) ? [{ path: '/timesheet/team', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, label: 'Team Timesheets' }] : []),
              ...(user?.role && (user.role === UserRole.SUPER_ADMIN || user.role === UserRole.HR_ADMIN) ? [{ path: '/timesheet/projects', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>, label: 'Projects & Tasks' }] : []),
            ]}
            triggerRef={timesheetTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'timesheet'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          {/* The same two children when the sidebar is collapsed to icons. */}
          <SidebarFlyout
            items={certificationMenuItems.map(item => ({
              path: item.path,
              icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
              label: item.label,
            }))}
            triggerRef={certificationsTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'certifications'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          <SidebarFlyout
            items={nominationMenuItems.map(item => ({
              path: item.path,
              icon: (() => {
                const iconMap: Record<string, React.ReactElement> = {
                  '/nominations/feed': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2"/></svg>,
                  '/nominations/my': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
                  '/nominations/new': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
                  '/voting': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
                  '/nominations/review': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
                  '/nominations/admin': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
                };
                return iconMap[item.path] || <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>;
              })(),
              label: item.label,
            }))}
            triggerRef={nominationsTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'nominations'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          <SidebarFlyout
            items={pmsMenuItems.map(item => ({
              path: item.path,
              icon: (() => {
                const iconMap: Record<string, React.ReactElement> = {
                  '/performance/objectives': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
                  '/performance/reviews': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
                  '/performance/team': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
                  '/performance/approvals': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
                  '/performance/team-reviews': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
                  '/performance/annual-cycle': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
                  '/performance/analytics': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
                  '/performance/review-cycles': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
                  '/performance/reports': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
                  '/performance/lock-management': <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
                };
                return iconMap[item.path] || <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>;
              })(),
              label: item.label,
            }))}
            triggerRef={performanceTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'performance'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          <SidebarFlyout
            items={[
              // Same gate as the expanded submenu — the flyout is the collapsed-sidebar copy of
              // that list, so leaving it ungated would put the link back for exactly the roles
              // the other branch just hid it from.
              { path: '/shift-roster/shift-details', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></svg>, label: 'Shift Details' },
              ...(user?.role && canManageShiftMaster(user.role) ? [
                { path: '/shift-roster/shift-configuration', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>, label: 'Shift Configuration' },
              ] : []),
              ...(user?.role && canAccessShiftRoster(user.role) ? [
                { path: '/shift-roster/bulk-override', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>, label: 'Bulk Override' },
              ] : []),
              ...(user?.role && canAccessShiftReports(user.role) ? [
                { path: '/shift-roster/reports', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></svg>, label: 'Reports' },
              ] : []),
            ]}
            triggerRef={shiftRosterTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'shiftRoster'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
          <SidebarFlyout
            items={[
              { path: '/attendance/my-attendance', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>, label: 'My Attendance' },
              // Same gate as the expanded submenu: the flyout is its collapsed-sidebar copy.
              { path: '/attendance/regularization', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, label: 'Regularization' },
              // Same gates as the expanded submenu; this flyout is its collapsed-sidebar copy.
              ...(attendanceTeam ? [
                { path: '/attendance/admin', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>, label: teamAttendanceLabel },
              ] : []),
              ...(attendanceAdmin ? [
                { path: '/attendance/raw-punches', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>, label: 'Raw Punches' },
                { path: '/attendance/device-sync', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>, label: 'Device Sync' },
                { path: '/attendance/reports', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="4" width="3" height="14"/></svg>, label: 'Reports' },
              ] : []),
            ]}
            triggerRef={attendanceTriggerRef as React.RefObject<HTMLElement>}
            isOpen={activeFlyout === 'attendance'}
            onClose={closeFlyout}
            onNavigate={navigate}
            activePath={location.pathname}
          />
        </>
      )}
    </>
  );
};

export default Sidebar;
